/**
 * Transaction Types
 * @fileoverview TypeScript interfaces for Plaid transactions
 */

/**
 * Transaction classification (income vs expense)
 */
export type TransactionClass = 'INCOME' | 'EXPENSE';

/**
 * Plaid Account summary for transaction display
 */
export interface PlaidAccountSummary {
  id: string;
  name: string;
  accountId: string;
  officialName?: string | null;
  type?: string;
  subtype?: string | null;
  mask?: string | null;
  plaidItem?: {
    institutionName?: string | null;
  };
}

/**
 * Full Plaid Transaction model matching database schema
 */
export interface PlaidTransaction {
  id: string;
  tenantId: string;
  plaidItemId: string;
  plaidAccountId: string;
  transactionId: string;
  pendingTransactionId: string | null;

  // Core Transaction Data
  amount: number;
  isoCurrencyCode: string | null;
  unofficialCurrencyCode: string | null;
  date: string;
  datetime: string | null;
  authorizedDate: string | null;
  authorizedDatetime: string | null;
  name: string;
  originalDescription: string | null;
  pending: boolean;

  // Merchant Information
  merchantName: string | null;
  normalizedMerchant?: string | null;
  normalizationStatus?: string | null;
  normalizationSource?: string | null;
  merchantEntityId: string | null;
  logoUrl: string | null;
  website: string | null;

  // Categorization (legacy)
  categoryId: string | null;
  category: string[];

  // Personal Finance Category (v2)
  personalFinanceCategoryPrimary: string | null;
  personalFinanceCategoryDetailed: string | null;
  personalFinanceCategoryConfidenceLevel: string | null;
  personalFinanceCategoryIconUrl: string | null;

  // Location
  locationAddress: string | null;
  locationCity: string | null;
  locationRegion: string | null;
  locationPostalCode: string | null;
  locationCountry: string | null;
  locationLat: number | null;
  locationLon: number | null;
  locationStoreNumber: string | null;

  // Payment Metadata
  paymentChannel: string | null;
  transactionCode: string | null;
  transactionType: string | null;
  transactionClass: TransactionClass;
  checkNumber: string | null;

  // Counterparties
  counterparties: unknown | null;

  // Sync Metadata
  retrievedAt: string;
  syncCursor: string | null;
  isRemoved: boolean;
  removedAt: string | null;
  createdAt: string;
  updatedAt: string;

  // Related account (included when fetching transactions)
  // Related account (included when fetching transactions)
  plaidAccount?: PlaidAccountSummary;
  
  // Snapshot/Frontend specific fields
  isModified?: boolean;
  /** Original Plaid transaction UUID - used for navigation in editable/snapshot mode */
  originalPlaidTransactionId?: string;
}

/**
 * Filters for querying transactions
 */
export interface TransactionFilters {
  accountId?: string;
  accountIds?: string[];
  dateFrom?: string;
  dateTo?: string;
  amountMin?: number;
  amountMax?: number;
  merchantName?: string;
  pfcPrimary?: string;
  pfcDetailed?: string;
  category?: string;
  transactionClass?: TransactionClass;
}

/**
 * Sort configuration for transactions
 */
export interface TransactionSort {
  column: TransactionSortColumn;
  direction: 'asc' | 'desc';
}

/**
 * Sortable columns
 */
export type TransactionSortColumn =
  | 'transactionId'
  | 'datetime'
  | 'date'
  | 'checkNumber'
  | 'isoCurrencyCode'
  | 'amount'
  | 'name'
  | 'originalDescription'
  | 'merchantName'
  | 'pending'
  | 'personalFinanceCategoryPrimary'
  | 'personalFinanceCategoryDetailed'
  | 'category'
  | 'locationPostalCode'
  | 'paymentChannel'
  | 'transactionCode'
  | 'transactionType';

/**
 * Selection state for transaction checkboxes
 */
export interface TransactionSelectionState {
  selectedIds: Set<string>;
  selectAllMode: 'none' | 'displayed' | 'all';
}

/**
 * API response for paginated transactions
 */
export interface PaginatedTransactionsResponse {
  transactions: PlaidTransaction[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

/**
 * Column definition for transaction table
 */
export interface TransactionColumnDef {
  key: string;
  label: string;
  sortable: boolean;
  width: string;
  field?: keyof PlaidTransaction | 'plaidAccount.name' | 'actions';
}

/**
 * Result of a transaction sync operation
 */
export interface TransactionSyncResult {
  success: boolean;
  added: number;
  modified: number;
  removed: number;
  cursor?: string;
  syncId?: string;
  durationMs?: number;
  error?: string;
}

/**
 * Sync operation status
 */
export type SyncStatus = 'idle' | 'syncing' | 'success' | 'error';

/**
 * Result of a transaction reset/delete operation
 */
export interface TransactionResetResult {
  deletedTransactions: number;
  resetComplete: boolean;
}

/**
 * Format account for human-readable display
 * Output: "{Institution} · {Type} ****{Mask}" or fallback to name
 *
 * @param account - PlaidAccountSummary or undefined
 * @returns Formatted string like "Chase · Checking ****1234"
 */
export function formatAccountDisplay(account: PlaidAccountSummary | undefined | null): string {
  if (!account) return '-';

  const parts: string[] = [];

  // Institution name (bank)
  if (account.plaidItem?.institutionName) {
    parts.push(account.plaidItem.institutionName);
  }

  // Account type (capitalize first letter, replace underscores)
  const accountType = account.subtype || account.type;
  if (accountType) {
    const formatted = accountType
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
    parts.push(formatted);
  }

  // Masked account number
  if (account.mask) {
    parts.push(`****${account.mask}`);
  }

  return parts.length > 0 ? parts.join(' · ') : account.name;
}
